/**
 * @file useFontsLink.ts
 * @description Ссылка «Сменить шрифт ›» из подсказки о шрифте элемента
 * (phase19.16, приёмка Р2а, §7.17 п. 3; uiux-fonts-usage §4): «Оформление →
 * Шрифты» с раскрытой ролью и возвратом туда, где человек был.
 *
 * Контракт с вкладкой «Шрифты» — параметры адреса: `font=<роль>` (раскрыть
 * роль), `back=<адрес конструктора>` и `backLabel=<«Заголовок (секция
 * «Новинки»)»>` для ссылки «← К элементу …». Выбор элемента в адрес не
 * попадает — возврат ведёт к блоку, разделу или вкладке.
 *
 * В конструкторе переход идёт через `select`: несохранённый блок спрашивает
 * «Сохранить / Не сохранять / Остаться» (`useBlockLeaveGuard`), как любой
 * другой переход. Вне конструктора (тесты) — обычная ссылка.
 */

import type { FontRole } from "@chaiberi/shared";
import { useContext, type MouseEvent } from "react";
import { useSearchParams } from "react-router";

import { useCurrentContext } from "../../auth/hooks";
import { BuilderContext } from "../../components/site-builder/builder-context";
import { builderLink, parseSelection, type BuilderSelection } from "../../lib/site-builder/selection";

/** Раздел «Оформление → Шрифты». */
const FONTS_PANEL: Partial<BuilderSelection> = { panel: "appearance", sub: "fonts" };

export interface FontsLink {
  to: string;
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * @param role роль, которую раскрыть во вкладке
 * @param backLabel подпись возврата: «Заголовок (секция «Новинки»)»
 * @param back куда вернуться, если не на текущий адрес (вкладка каталога)
 */
export function useFontsLink(role: FontRole, backLabel: string, back?: Partial<BuilderSelection>): FontsLink {
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const [params] = useSearchParams();
  const builder = useContext(BuilderContext);
  const current = parseSelection(params);
  const extra: Record<string, string> = { font: role, back: builderLink(slug, { ...current, ...back }), backLabel };
  // Адрес раздела — общим builderLink, служебные параметры — поверх него; открытая группа (SB122) не переезжает.
  const base = builderLink(slug, { ...current, ...FONTS_PANEL, version: null, group: null });
  const [path = base, search = ""] = base.split("?", 2);
  const query = new URLSearchParams(search);
  for (const [key, value] of Object.entries(extra)) query.set(key, value);

  return {
    to: `${path}?${query.toString()}`,
    onClick: (event) => {
      if (!builder) return;
      event.preventDefault();
      void builder.select(FONTS_PANEL, extra);
    },
  };
}
